import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosClient } from '@/shared/apis/config';
import type { CaptionTranslation } from '@/shared/apis/editor/clip-types';
import { ApiResponse, ErrorResponse } from '@/shared/types';

/**
 * 에디터 온디맨드 자막 번역 API.
 *
 * 생성 시 번역을 켜지 않은 프로젝트나, 자막 확장으로 새로 생긴 캡션처럼 번역이 없는
 * 조각을 에디터에서 직접 번역한다. BE 는 정책(`captionTranslationEnabled`)을 검증한 뒤
 * ai-api 로 넘기고 결과만 돌려준다(영속 없음 — 에디터 history 가 SoT).
 */

/** 슬롯 안의 단어 1개. start/end 는 캡션 시작 0-based 상대 ms. */
export interface CaptionTranslationSlotWord {
  word: string;
  start: number;
  end: number;
  speaker?: string[];
}

/** 번역 대상 슬롯 1개. `key` 는 caption asset id 이며 응답 매칭에 그대로 쓰인다. */
export interface CaptionTranslationSlot {
  key: string;
  words: CaptionTranslationSlotWord[];
}

/** 번역 대상 제목. `key` 는 title text item id 이며 응답 매칭에 그대로 쓰인다. */
export interface CaptionTranslationTitle {
  key: string;
  text: string;
}

export interface PostCaptionTranslationRequest {
  projectId: number;
  targetLanguage: string;
  storyId?: number;
  slots: CaptionTranslationSlot[];
  titles: CaptionTranslationTitle[];
}

export interface CaptionTranslationSlotResult {
  key: string;
  translation: CaptionTranslation;
}

export interface PostCaptionTranslationResponse {
  slots: CaptionTranslationSlotResult[];
  /** 성공한 제목만 포함된다. 실패한 key 는 생략되어 원문을 유지한다. */
  titles: CaptionTranslationTitle[];
}

type ResponseType = ApiResponse<PostCaptionTranslationResponse>;

export const postCaptionTranslation = async ({
  projectId,
  ...body
}: PostCaptionTranslationRequest) => {
  const response = await axiosClient.post<ResponseType>(
    `/v2.0/project/${projectId}/caption-translation`,
    body,
  );
  return response.data;
};

export const usePostCaptionTranslationApi = () => {
  return useMutation<
    ResponseType,
    AxiosError<ErrorResponse>,
    PostCaptionTranslationRequest
  >({
    mutationFn: postCaptionTranslation,
  });
};
